import { AVAILABLE_MODEL_VERSIONS, MODEL_VERSION, MODEL_VERSION_SETTINGS_MAP } from '@/app/constants';
import type { FormTabType, ModelVersion, ReportParams, TaskInfo, TaskSettings } from '@/app/types';
import { isImageTask } from './task-types';
import { getAvailableOptions } from './validate-settings';

const CREATION_MODE_MAP: Partial<Record<NonNullable<TaskSettings['transition']>, ReportParams['creation_mode']>> = {
  pro: 'cinematic',
  speed: 'flash',
};

/**
 * 类型守卫：检查字符串是否为有效的 ModelVersion
 *
 * @param value - 待检查的字符串
 * @returns 如果是有效的 ModelVersion 则返回 true，否则返回 false
 */
const isValidModelVersion = (value: string | undefined): value is ModelVersion => {
  if (!value) return false;
  return Object.keys(MODEL_VERSION_SETTINGS_MAP).includes(value);
};

/**
 * 类型守卫：检查类型是否为有效的 FormTabType（排除 scene）
 *
 * @param value - 待检查的类型
 * @returns 如果是有效的 FormTabType（排除 scene）则返回 true，否则返回 false
 */
const isValidFormTabType = (value: TaskInfo['type']): value is Exclude<FormTabType, 'scene'> => {
  return Object.keys(AVAILABLE_MODEL_VERSIONS).includes(value);
};

/**
 * 检查指定模型版本和表单类型是否支持智能宽高比
 *
 * @param modelVersion - 模型版本（字符串类型）
 * @param type - 表单类型
 * @returns 如果支持智能宽高比则返回 true，否则返回 false
 */
const isIntelligentAspectRatioAvailable = (modelVersion: string | undefined, type: TaskInfo['type']): boolean => {
  if (!modelVersion) return false;
  if (!isValidModelVersion(modelVersion)) return false;
  if (!isValidFormTabType(type)) return false;
  const availableAspectRatio = getAvailableOptions(modelVersion, type).aspect_ratio || [];
  return availableAspectRatio.includes('intelligent');
};

export const getReportParams = (
  type: TaskInfo['type'],
  settings?: TaskInfo['settings'],
  templateScene?: string,
): ReportParams => {
  const isIntelligentAspectRatio = isIntelligentAspectRatioAvailable(settings?.model_version, type);

  return {
    style: settings?.style || '',
    duration: settings?.duration || 0,
    task_type: type,
    model_version: settings?.model_version === '3.1_pro' ? '3.1.1' : settings?.model_version || '', // 埋点上报类型为 Version，3.1_pro 需要上报为 3.1.1
    resolution: settings?.resolution || '',
    aspect_ratio:
      settings?.aspect_ratio === '' && isIntelligentAspectRatio ? 'intelligent' : settings?.aspect_ratio || '',
    movement_amplitude: settings?.movement_amplitude || '',
    templates_name: templateScene || '',
    non_peak_mode: settings?.schedule_mode === 'nopeak',
    content_type: isImageTask({ type }) ? 'image' : 'video',
    creation_mode:
      settings?.model_version === MODEL_VERSION.Q2 && settings?.transition
        ? CREATION_MODE_MAP[settings.transition]
        : undefined,
  };
};
